import { createEffect, createSignal, For, Show, untrack } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Building2, Check, ChevronDown, Plus } from "@fleet/icons";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useWorkspace } from "@/features/chronos-sync/workspace-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { cn } from "@time-tracker/shared";
import { msg } from "@lingui/core/macro";
import { useI18n } from "@/providers/i18n-provider";

/**
 * Active workspace selector for Chronos screens, with creation of
 * new workspaces. All collaborative data (projects, team, feed,
 * tags) is filtered by the workspace selected here.
 */
export function WorkspaceSwitcher() {
  const { workspaces, activeWorkspaceId, activeWorkspace, setActiveWorkspace, createWorkspace } =
    useWorkspace();
  const drizzle = useDrizzle();
  const { t } = useI18n();

  // Deep-link to the "create workspace" dialog: `?wsdialog=new`. We use our own
  // param name (not `dialog`) to avoid clashing with each screen's dialogs,
  // since the switcher renders on several chronos routes. `strict:false`+`to:"."`
  // preserve the other params (e.g. `?ws=`).
  const search = useSearch({ strict: false });
  const navigate = useNavigate();

  const wantCreateOpen = (): boolean => (search() as { wsdialog?: unknown }).wsdialog === "new";

  const setCreateUrl = (open: boolean) => {
    void navigate({
      to: ".",
      search: (prev) => ({ ...prev, wsdialog: open ? "new" : undefined }),
      replace: true,
    });
  };

  const [isMenuOpen, setIsMenuOpen] = createSignal(false);
  const [isCreateOpen, setIsCreateOpen] = createSignal(wantCreateOpen());
  const [newName, setNewName] = createSignal("");
  const [isCreating, setIsCreating] = createSignal(false);

  // URL → state (pasted link, back/forward). Idempotent. The comparison is inside
  // `untrack`: it reacts only to EXTERNAL URL changes; if it tracks
  // `isCreateOpen()`, opening the dialog state-first retriggers it with the old URL
  // and closes it immediately (bug #15b / CC-16).
  createEffect(() => {
    const want = wantCreateOpen();
    if (untrack(() => want !== isCreateOpen())) {
      setIsCreateOpen(want);
    }
  });

  const openCreate = () => {
    setIsMenuOpen(false);
    setIsCreateOpen(true);
    setCreateUrl(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    setCreateUrl(false);
  };

  const handleCreate = async (e: Event) => {
    e.preventDefault();
    if (!newName().trim()) {
      return;
    }
    setIsCreating(true);
    try {
      const id = await createWorkspace(newName());
      if (id) {
        setIsCreateOpen(false);
        setCreateUrl(false);
        setNewName("");
      }
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <>
      <Popover open={isMenuOpen()} onOpenChange={setIsMenuOpen}>
        <PopoverTrigger>
          <button
            type="button"
            class="flex items-center gap-2 bg-[#121212] border border-white/10 rounded-full px-3.5 py-2 text-xs font-mono text-foreground hover:border-white/20 transition-colors cursor-pointer max-w-[220px]"
          >
            <Building2 class="h-3.5 w-3.5 text-[#afc6ff] shrink-0" />
            <span class="truncate">{activeWorkspace()?.name ?? t(msg`Workspace`)}</span>
            <ChevronDown class="h-3 w-3 text-muted-foreground shrink-0" />
          </button>
        </PopoverTrigger>
        <PopoverContent class="bg-[#1a1a1a]/95 backdrop-blur-xl border border-white/10 rounded-[16px] p-1.5 shadow-2xl w-60 z-50">
          <span class="text-[9px] font-bold text-muted-foreground uppercase tracking-widest font-mono block px-2 py-1.5">
            {t(msg`My Workspaces`)}
          </span>
          <For each={workspaces()}>
            {(ws) => (
              <button
                type="button"
                onClick={() => {
                  setActiveWorkspace(ws.id);
                  setIsMenuOpen(false);
                }}
                class={cn(
                  "w-full flex items-center justify-between gap-2 p-2 rounded-lg text-left hover:bg-white/5 transition-colors cursor-pointer text-xs font-mono",
                  ws.id === activeWorkspaceId() ? "text-[#afc6ff]" : "text-foreground",
                )}
              >
                <span class="truncate">{ws.name}</span>
                <Show when={ws.id === activeWorkspaceId()}>
                  <Check class="h-3.5 w-3.5 shrink-0" />
                </Show>
              </button>
            )}
          </For>
          <div class="border-t border-white/5 mt-1 pt-1">
            <button
              type="button"
              onClick={openCreate}
              class="w-full flex items-center gap-2 p-2 rounded-lg text-left hover:bg-white/5 transition-colors cursor-pointer text-xs font-mono text-muted-foreground hover:text-foreground"
            >
              <Plus class="h-3.5 w-3.5" /> {t(msg`New workspace`)}
            </button>
          </div>
        </PopoverContent>
      </Popover>

      <Dialog
        open={isCreateOpen()}
        onOpenChange={(open) => {
          setIsCreateOpen(open);
          setCreateUrl(open);
        }}
        contentClass="bg-[#121212] border border-white/10 rounded-[24px] p-0 overflow-hidden max-w-sm"
      >
        <div class="p-6 text-white">
          <DialogHeader showClose border={false} class="p-0 mb-4 text-white">
            <DialogTitle class="text-xl font-bold font-mono">{t(msg`New Workspace`)}</DialogTitle>
            <DialogDescription class="text-xs text-white/60 font-mono mt-1">
              {t(msg`An independent space with its own projects, team and tags.`)}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreate} class="space-y-5">
            <div class="space-y-1">
              <label
                for="new-workspace-name"
                class="text-[9px] font-bold text-white/45 uppercase tracking-widest font-mono"
              >
                {t(msg`Name`)}
              </label>
              <input
                id="new-workspace-name"
                type="text"
                placeholder={t(msg`e.g. Acme Client`)}
                value={newName()}
                onInput={(e) => setNewName(e.currentTarget.value)}
                class="w-full bg-black/40 border border-white/10 rounded-[16px] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#afc6ff]/40 text-white placeholder-white/20 font-mono"
              />
            </div>

            <div class="flex justify-end gap-3 pt-1">
              <Button
                type="button"
                variant="ghost"
                onClick={closeCreate}
                class="px-5 py-2.5 rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer border border-white/5 hover:bg-white/5"
              >
                {t(msg`Cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={isCreating() || !newName().trim() || !drizzle()}
                class="px-6 py-2.5 bg-primary text-black rounded-full font-bold font-mono text-xs uppercase tracking-wider cursor-pointer shadow-[inset_0_1px_0_rgba(255,255,255,0.2)] hover:bg-primary/95"
              >
                <Show when={!isCreating()} fallback={t(msg`Creating...`)}>
                  {t(msg`Create`)}
                </Show>
              </Button>
            </div>
          </form>
        </div>
      </Dialog>
    </>
  );
}
